import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import clsx from 'clsx';

import { Drawer, drawerType } from '../../../../../../packages/ui/organisms/drawer';
import {
  btnType,
  Button,
  colors,
  fontWeight,
  IconButton,
  Icons,
  Stepper,
  Text,
} from '../../../../../../packages';
import StepOne from '../../AddNewReport/step-one/StepOne';
import StepTwo from './step-two/StepTwo';
import StepThree from './step-three/StepThree';
import { stepTitles } from '../Medication.constants';
import { useStyles } from './AddNewReport.styles';

const AddNewReport = ({
  isOpen,
  isNoDrawer,
  onClose,
  options,
  onSubmit,
  lookupData,
  memberId,
  isLoadingOptions = false,
}) => {
  const classes = useStyles();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [selectedMedication, setSelectedMedication] = React.useState([]);
  const [selectedItemsForm, setSelectedItemsForm] = React.useState([]);

  const onCloseClick = () => {
    if (currentStep === 0) {
      onClose();
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const onRemoveItem = index => {
    const newSymptom = selectedMedication.filter((_, i) => i !== index);
    if (newSymptom.length === 0) {
      setCurrentStep(0);
    }
    setSelectedMedication(newSymptom);
  };

  const onClickContinue = () => {
    if (currentStep === 0) {
      const newMedications = selectedMedication.map(item => {
        let groupId = null;
        let itemOpt = null;
        options.forEach(opt => {
          const result = opt.items.find(it => it.value === item);
          if (result) {
            groupId = opt.groupId;
            itemOpt = result;
          }
        });
        return {
          groupId,
          item: itemOpt,
          status: '',
          notes: '',
          isInterference: false,
        };
      });
      setSelectedItemsForm(newMedications);
    }
    if (currentStep === 2) {
      onSubmit(selectedItemsForm);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      setSelectedMedication([]);
    }
  }, [isOpen]);

  const renderStep = step => {
    switch (step) {
      case 0:
        return (
          <StepOne
            selectedItems={selectedMedication}
            setSelectedItems={setSelectedMedication}
            options={options}
            searchLabel="Select one or more medications"
          />
        );
      case 1:
        return (
          <StepTwo
            selectedItems={selectedItemsForm}
            updateItems={setSelectedItemsForm}
            onRemoveItem={onRemoveItem}
            lookupData={lookupData}
            showInterference
            memberId={memberId}
            options={options}
          />
        );
      case 2:
        return <StepThree selectedMedications={selectedItemsForm} />;
      default:
        return null;
    }
  };

  const checkDisabled = () => {
    if (currentStep === 0) {
      return selectedMedication.length === 0;
    }
    if (currentStep === 1) {
      return selectedItemsForm.some(item => item.notes.trim().length === 0);
    }
    return false;
  };

  const renderContent = () => (
    <Box className={classes.wrapper}>
      <Box className={clsx(classes.header, { [classes.headerNoDrawer]: isNoDrawer })}>
        <Box>
          <Text weight={fontWeight.BOLD} className={classes.headTitle}>
            Report new medication
          </Text>
          <Text weight={fontWeight.BOLD} className={classes.headSubtitle}>
            {stepTitles[currentStep]}
          </Text>
        </Box>
        <Box className={classes.stepWrap}>
          <Stepper steps={['Select', 'Detail', 'Review']} activeStep={currentStep} />
        </Box>
        <IconButton icon="close" className={classes.closeBtn} onClick={onClose} />
      </Box>
      <Box
        className={clsx(classes.content, {
          [classes.contentNoDrawer]: isNoDrawer,
          [classes.contentStep2]: currentStep > 0,
        })}
      >
        {isLoadingOptions ? (
          <Box display="flex" justifyContent="center" alignItems="center" flexDirection="row" gap={4}>
            <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
            Loading...
          </Box>
        ) : (
          renderStep(currentStep)
        )}
      </Box>

      <Box className={classes.footer}>
        <Button variant={btnType.TEXT} onClick={onCloseClick}>
          Cancel
        </Button>
        <Button onClick={onClickContinue} disabled={checkDisabled()}>
          {currentStep === 2 ? 'Send report' : 'Continue'}
        </Button>
      </Box>
    </Box>
  );

  if (isNoDrawer) {
    return renderContent();
  }

  return (
    <Drawer open={isOpen} onClose={onCloseClick} variant={drawerType.NORMAL} className={classes.drawer}>
      {renderContent()}
    </Drawer>
  );
};

export { AddNewReport };
